/**
 * Diagnóstico del arranque y de los fallos en el navegador de quien usa
 * Andarama, para el Studio y para el visor publicado.
 *
 * Se apunta una línea de tiempo (cada hito con sus milisegundos desde que
 * empezó la página) y se manda a la analítica (`/ingest/e`) como evento
 * `diag` en momentos fijos: al quedar lista la página, cuando a los 10, 25 y
 * 60 s todavía no lo está, al irse de la página y ante cada error. Los
 * errores van además sueltos como `client_error`. Así un arranque que se
 * cuelga en unas gafas deja dicho dónde se quedó, cosa que antes no pasaba.
 *
 * Nada de datos de la persona: ni correo, ni nombres de proyecto, ni
 * parámetros de las URL; los identificadores largos de las rutas se recortan.
 *
 * Con `?diag=1` en la dirección (se recuerda en la pestaña; `?diag=0` lo
 * quita) la misma línea de tiempo se ve en pantalla, para probar en las gafas.
 */

export type DiagApp = "studio" | "viewer";

export interface DiagEntry {
  /** Milisegundos desde el arranque de la página. */
  t: number;
  label: string;
  detail?: string;
}

export interface DiagTransport {
  send(events: Record<string, unknown>[]): void;
}

/** Hitos que se guardan como mucho (se quedan los primeros y los últimos). */
const MAX_ENTRIES = 120;
/** Envíos como mucho por página: un error en bucle no inunda la analítica. */
const MAX_SENDS = 25;
/** Tamaño máximo del detalle que acepta la ingesta. */
export const DIAG_META_MAX = 4000;
const STALL_CHECKS_MS = [10_000, 25_000, 60_000];

export class DiagCore {
  readonly entries: DiagEntry[] = [];
  private sends = 0;
  private dropped = 0;
  private envSent = false;
  ready = false;
  private listeners = new Set<(e: DiagEntry) => void>();

  constructor(
    readonly app: DiagApp,
    readonly slug: string,
    readonly sessionId: string,
    readonly device: string,
    private transport: DiagTransport | null,
    private now: () => number,
    private env: () => Record<string, unknown>,
    private lang: () => string = () => "",
  ) {}

  onEntry(fn: (e: DiagEntry) => void): void {
    this.listeners.add(fn);
  }

  mark(label: string, detail?: unknown): void {
    const entry: DiagEntry = { t: Math.round(this.now()), label: label.slice(0, 80) };
    const d = detail == null ? undefined : typeof detail === "string" ? detail : safeJson(detail);
    if (d != null && d !== "") entry.detail = d.slice(0, 300);
    if (this.entries.length >= MAX_ENTRIES) {
      // Se conservan los primeros (el arranque) y se rota el resto
      this.entries.splice(40, 1);
      this.dropped++;
    }
    this.entries.push(entry);
    for (const fn of this.listeners) fn(entry);
  }

  /** Un fallo: hito, evento suelto `client_error` y la línea de tiempo. */
  error(stage: string, err: unknown): void {
    const detail = describeError(err);
    this.mark(`error:${stage}`, detail);
    this.sendRaw("client_error", { stage, detail: detail.slice(0, 1500), app: this.app, t: Math.round(this.now()) });
    this.flush(`error:${stage}`);
  }

  /** La página ya funciona: se manda la línea de tiempo del arranque. */
  markReady(what: string): void {
    if (this.ready) return;
    this.ready = true;
    this.mark(`ready:${what}`);
    this.flush("ready");
  }

  /** Comprobación de arranque colgado: solo envía si aún no está lista. */
  stallCheck(ms: number, snapshot?: () => Record<string, unknown>): void {
    if (this.ready) return;
    this.mark(`stall:${Math.round(ms / 1000)}s`, snapshot?.());
    this.flush(`stall:${Math.round(ms / 1000)}s`);
  }

  flush(phase: string): void {
    this.sendRaw("diag", this.payload(phase));
  }

  /** El detalle de un envío `diag`, recortado para caber en la ingesta. */
  payload(phase: string): Record<string, unknown> {
    const base: Record<string, unknown> = { phase, app: this.app, ready: this.ready };
    if (!this.envSent) base.env = this.env();
    if (this.dropped > 0) base.dropped = this.dropped;
    // Línea de tiempo compacta: «ms label · detalle»
    let tl = this.entries.map((e) => `${e.t} ${e.label}${e.detail != null ? ` · ${e.detail}` : ""}`);
    const fits = (): boolean => JSON.stringify({ ...base, tl }).length <= DIAG_META_MAX - 50;
    // Si no cabe: primero se recortan los detalles; luego se quitan hitos del medio
    if (!fits()) tl = this.entries.map((e) => `${e.t} ${e.label}${e.detail != null ? ` · ${e.detail.slice(0, 60)}` : ""}`);
    if (!fits() && base.env != null) base.env = safeJson(base.env).slice(0, 900);
    while (!fits() && tl.length > 12) tl.splice(6, 1);
    base.tl = tl;
    return base;
  }

  private sendRaw(event: "diag" | "client_error", meta: Record<string, unknown>): void {
    if (this.transport == null || this.sends >= MAX_SENDS) return;
    this.sends++;
    if (event === "diag" && meta.env != null) this.envSent = true;
    this.transport.send([
      { t: this.slug, e: event, d: this.device, sid: this.sessionId, l: this.lang(), m: safeJson(meta).slice(0, DIAG_META_MAX) },
    ]);
  }
}

export function describeError(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}${err.stack != null ? `\n${err.stack.split("\n").slice(1, 6).join("\n")}` : ""}`;
  if (typeof err === "string") return err;
  return safeJson(err);
}

function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v) ?? String(v);
  } catch {
    return String(v);
  }
}

/** Ruta sin parámetros y con los identificadores largos recortados. */
export function cleanPath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => (seg.length > 14 && /[A-Za-z]/.test(seg) && /\d/.test(seg) ? `${seg.slice(0, 4)}…` : seg))
    .join("/")
    .slice(0, 120);
}

// ---------------------------------------------------------------------------
// Navegador
// ---------------------------------------------------------------------------

export function deviceKind(): string {
  const ua = navigator.userAgent;
  if (/Quest|OculusBrowser|Pico|Wolvic|Mobile VR/i.test(ua)) return "vr";
  if (/iPad|Tablet/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua))) return "tablet";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  return "desktop";
}

/** El identificador efímero de la pestaña, el mismo que usa la analítica del visor. */
export function tabSessionId(): string {
  try {
    let id = sessionStorage.getItem("andarama:sid");
    if (id == null) {
      id = crypto.randomUUID();
      sessionStorage.setItem("andarama:sid", id);
    }
    return id;
  } catch {
    try {
      return crypto.randomUUID();
    } catch {
      return `s-${Date.now().toString(36)}`;
    }
  }
}

function webglInfo(): Record<string, unknown> {
  try {
    const canvas = document.createElement("canvas");
    const gl2 = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
    const gl = (gl2 ?? canvas.getContext("webgl")) as WebGLRenderingContext | null;
    if (gl == null) return { webgl: false };
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const info = {
      webgl: gl2 != null ? 2 : 1,
      maxTex: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxCube: gl.getParameter(gl.MAX_CUBE_MAP_TEXTURE_SIZE),
      renderer: String(ext != null ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER)).slice(0, 80),
    };
    gl.getExtension("WEBGL_lose_context")?.loseContext();
    return info;
  } catch (err) {
    return { webgl: `error:${String(err).slice(0, 80)}` };
  }
}

function browserEnv(): Record<string, unknown> {
  const n = navigator as Navigator & {
    deviceMemory?: number;
    connection?: { effectiveType?: string; downlink?: number; saveData?: boolean };
    xr?: unknown;
    standalone?: boolean;
  };
  let storage = true;
  try {
    localStorage.setItem("andarama:diag:probe", "1");
    localStorage.removeItem("andarama:diag:probe");
  } catch {
    storage = false;
  }
  const nav = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
  return {
    ua: n.userAgent.slice(0, 300),
    lang: n.language,
    langs: (n.languages ?? []).slice(0, 4).join(","),
    screen: `${screen.width}x${screen.height}@${devicePixelRatio}`,
    vp: `${innerWidth}x${innerHeight}`,
    cores: n.hardwareConcurrency,
    mem: n.deviceMemory,
    net: n.connection != null ? `${n.connection.effectiveType ?? "?"}/${n.connection.downlink ?? "?"}${n.connection.saveData === true ? "/save" : ""}` : undefined,
    online: n.onLine,
    cookies: n.cookieEnabled,
    storage,
    sw: n.serviceWorker?.controller != null,
    standalone: matchMedia?.("(display-mode: standalone)").matches === true || n.standalone === true,
    xr: n.xr != null,
    secure: typeof isSecureContext === "undefined" ? null : isSecureContext,
    framed: (() => {
      try {
        return window.top !== window;
      } catch {
        return true;
      }
    })(),
    path: cleanPath(location.pathname),
    ref: document.referrer !== "" ? (() => { try { return new URL(document.referrer).host; } catch { return "?"; } })() : undefined,
    navType: nav?.type,
    ttfb: nav != null ? Math.round(nav.responseStart) : undefined,
    tz: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch {
        return undefined;
      }
    })(),
    ...webglInfo(),
  };
}

function beaconTransport(endpoint: string): DiagTransport {
  return {
    send(events) {
      const payload = JSON.stringify({ events });
      try {
        if (navigator.sendBeacon?.(endpoint, new Blob([payload], { type: "application/json" })) === true) return;
      } catch {
        // sin sendBeacon: fetch
      }
      try {
        void origFetch?.(endpoint, { method: "POST", body: payload, headers: { "content-type": "application/json" }, keepalive: true }).catch(
          () => undefined,
        );
      } catch {
        // sin red
      }
    },
  };
}

let origFetch: typeof fetch | null = typeof fetch === "function" ? fetch.bind(globalThis) : null;
let current: DiagCore | null = null;

/** El diagnóstico de la página, si está instalado. */
export function diag(): DiagCore | null {
  return current;
}

/** Atajo: apunta un hito si hay diagnóstico. */
export function diagMark(label: string, detail?: unknown): void {
  current?.mark(label, detail);
}

export interface InstallDiagOptions {
  app: DiagApp;
  slug: string;
  /** null: sin envío (analítica apagada); el panel en pantalla sigue. */
  endpoint: string | null;
  /** Qué mirar si el arranque se cuelga (se apunta con el aviso). */
  snapshot?: () => Record<string, unknown>;
  /** Vigilar las peticiones a estas rutas (prefijos del mismo origen). */
  watchFetch?: string[];
}

/** Instala el diagnóstico una sola vez por página y lo devuelve. */
export function installDiag(opts: InstallDiagOptions): DiagCore {
  if (current != null) return current;
  const t0 = typeof performance !== "undefined" ? 0 : Date.now();
  const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now() - t0);
  const core = new DiagCore(
    opts.app,
    opts.slug,
    tabSessionId(),
    deviceKind(),
    opts.endpoint != null && opts.endpoint !== "" ? beaconTransport(opts.endpoint) : null,
    now,
    browserEnv,
    () => document.documentElement.lang || navigator.language,
  );
  current = core;
  core.mark("boot", `${opts.app} ${cleanPath(location.pathname)}`);
  setupOverlay(core);

  // Errores sin recoger y ficheros que no cargan
  addEventListener(
    "error",
    (event) => {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null;
      if (target != null && (target as unknown) !== window && target.tagName != null) {
        const url = target.src ?? target.href ?? "";
        core.error("resource", `${target.tagName.toLowerCase()} ${cleanUrl(url)}`);
        return;
      }
      core.error("js", (event as ErrorEvent).error ?? `${(event as ErrorEvent).message} @${(event as ErrorEvent).filename}:${(event as ErrorEvent).lineno}`);
    },
    true,
  );
  addEventListener("unhandledrejection", (event) => core.error("promise", (event as PromiseRejectionEvent).reason));

  // console.error de las librerías (React, Clerk): solo el hito, sin envío suelto
  let consoleErrors = 0;
  const origError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    if (consoleErrors++ < 15) core.mark("console.error", args.map((a) => (a instanceof Error ? describeError(a) : String(a))).join(" ").slice(0, 300));
    origError(...args);
  };

  // Vida de la página
  document.addEventListener("visibilitychange", () => core.mark(`visible:${document.visibilityState}`));
  addEventListener("online", () => core.mark("online"));
  addEventListener("offline", () => core.mark("offline"));
  addEventListener("pagehide", () => {
    core.mark("pagehide");
    core.flush("pagehide");
  });
  navigator.serviceWorker?.addEventListener?.("controllerchange", () => core.mark("sw:controllerchange"));
  if (document.readyState !== "loading") core.mark("dom:ready");
  else document.addEventListener("DOMContentLoaded", () => core.mark("dom:ready"));
  addEventListener("load", () => core.mark("window:load"));

  // Tareas largas: el Quest con poca CPU se nota aquí
  try {
    let longTasks = 0;
    new PerformanceObserver((list) => {
      for (const e of list.getEntries()) {
        if (longTasks++ < 10 && e.duration > 200) core.mark("longtask", `${Math.round(e.duration)}ms`);
      }
    }).observe({ type: "longtask", buffered: true });
  } catch {
    // sin longtask
  }
  // Recursos que responden con error (responseStatus: Chromium)
  try {
    new PerformanceObserver((list) => {
      for (const e of list.getEntries() as PerformanceResourceTiming[]) {
        const status = (e as PerformanceResourceTiming & { responseStatus?: number }).responseStatus;
        if (status != null && status >= 400) core.mark("res:error", `${status} ${cleanUrl(e.name)}`);
        else if (e.duration > 5000) core.mark("res:lento", `${Math.round(e.duration)}ms ${cleanUrl(e.name)}`);
      }
    }).observe({ type: "resource", buffered: true });
  } catch {
    // sin resource timing
  }

  if (opts.watchFetch != null && origFetch != null) wrapFetch(core, opts.watchFetch);

  for (const ms of STALL_CHECKS_MS) {
    setTimeout(() => core.stallCheck(ms, () => ({ ...defaultSnapshot(), ...opts.snapshot?.() })), ms);
  }
  return core;
}

function defaultSnapshot(): Record<string, unknown> {
  return {
    vis: document.visibilityState,
    body: (document.body?.innerText ?? "").replace(/\s+/g, " ").trim().slice(0, 80),
    nodes: document.getElementsByTagName("*").length,
    heap: (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory != null
      ? Math.round((performance as Performance & { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize / 1e6)
      : undefined,
  };
}

function cleanUrl(url: string): string {
  try {
    const u = new URL(url, location.href);
    return `${u.host === location.host ? "" : u.host}${cleanPath(u.pathname)}`;
  } catch {
    return url.slice(0, 120);
  }
}

/** Cada petición vigilada deja su estado y su tiempo; las lentas y las fallidas, siempre. */
function wrapFetch(core: DiagCore, prefixes: string[]): void {
  const base = origFetch!;
  let logged = 0;
  const wrapped = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    let url: URL | null = null;
    try {
      url = new URL(typeof input === "string" ? input : input instanceof URL ? input.href : input.url, location.href);
    } catch {
      url = null;
    }
    const watched =
      url != null && (url.origin === location.origin ? prefixes.some((p) => url!.pathname.startsWith(p)) : /clerk/.test(url.host));
    if (!watched) return base(input, init);
    const method = init?.method ?? (input instanceof Request ? input.method : "GET");
    const label = `${method} ${url!.origin === location.origin ? "" : url!.host}${cleanPath(url!.pathname)}`;
    const start = performance.now();
    try {
      const res = await base(input, init);
      const ms = Math.round(performance.now() - start);
      if (!res.ok || ms > 3000 || logged++ < 40) core.mark(res.ok ? "fetch" : "fetch:error", `${res.status} ${ms}ms ${label}`);
      return res;
    } catch (err) {
      core.mark("fetch:fallo", `${Math.round(performance.now() - start)}ms ${label} ${describeError(err).slice(0, 120)}`);
      throw err;
    }
  };
  globalThis.fetch = wrapped as typeof fetch;
}

// ---------------------------------------------------------------------------
// Panel en pantalla (?diag=1)
// ---------------------------------------------------------------------------

function overlayEnabled(): boolean {
  try {
    const q = new URLSearchParams(location.search).get("diag");
    if (q === "1") sessionStorage.setItem("andarama:diag", "1");
    if (q === "0") sessionStorage.removeItem("andarama:diag");
    return sessionStorage.getItem("andarama:diag") === "1";
  } catch {
    return new URLSearchParams(location.search).get("diag") === "1";
  }
}

function setupOverlay(core: DiagCore): void {
  if (!overlayEnabled()) return;
  const mount = (): void => {
    const box = document.createElement("div");
    box.setAttribute("data-anda-diag", "");
    box.setAttribute("aria-hidden", "true");
    box.style.cssText =
      "position:fixed;left:8px;bottom:8px;z-index:2147483647;max-width:min(96vw,720px);max-height:45vh;overflow:auto;" +
      "background:rgba(0,0,0,.82);color:#d8ffd8;font:13px/1.35 ui-monospace,Menlo,monospace;padding:8px 10px;border-radius:8px;" +
      "white-space:pre-wrap;word-break:break-all;pointer-events:auto";
    const head = document.createElement("div");
    head.style.cssText = "color:#ffd27a;margin-bottom:4px";
    head.textContent = `diag ${core.app} · sesión ${core.sessionId.slice(0, 8)} · ${core.device}`;
    const list = document.createElement("div");
    box.append(head, list);
    const add = (e: DiagEntry): void => {
      const line = document.createElement("div");
      if (e.label.startsWith("error") || e.label.includes("fallo") || e.label.startsWith("stall")) line.style.color = "#ff9a9a";
      if (e.label.startsWith("ready")) line.style.color = "#9affc2";
      line.textContent = `${(e.t / 1000).toFixed(2)}s ${e.label}${e.detail != null ? ` · ${e.detail}` : ""}`;
      list.appendChild(line);
      box.scrollTop = box.scrollHeight;
    };
    for (const e of core.entries) add(e);
    core.onEntry(add);
    document.body.appendChild(box);
  };
  if (document.body != null) mount();
  else document.addEventListener("DOMContentLoaded", mount);
}

/** Solo para pruebas: olvida el diagnóstico instalado. */
export function resetDiagForTests(): void {
  current = null;
  origFetch = typeof fetch === "function" ? fetch.bind(globalThis) : null;
}
